import { Button, IconButton, Slider } from "@material-ui/core";
import React, { useRef, useEffect, useState, useContext, useCallback } from "react";
import api from "../../services/api";
import { Typography } from "@material-ui/core";
import { useTheme, makeStyles } from "@material-ui/core/styles";
import PlayArrowIcon from "@material-ui/icons/PlayArrow";
import PauseIcon from "@material-ui/icons/Pause";
import { AudioPlayerContext } from "../../context/AudioPlayer/AudioPlayerContext";

const useStyles = makeStyles((theme) => ({
  audioContainer: {
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
    maxWidth: '380px',
    minWidth: '240px',
    padding: 0,
    margin: 0,
    backgroundColor: 'transparent',
    border: 'none'
  },
  playerRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 4,
    padding: '4px 0',
    width: '100%',
  },
  playButton: {
    padding: 6,
    color: theme.palette.primary.main,
  },
  slider: {
    flex: 1,
    marginLeft: 4,
    marginRight: 4,
    '& .MuiSlider-rail': {
      height: 3,
      borderRadius: 2,
      opacity: 0.3,
    },
    '& .MuiSlider-track': {
      height: 3,
      borderRadius: 2,
    },
    '& .MuiSlider-thumb': {
      width: 12,
      height: 12,
      marginTop: -4.5,
      '&:hover, &.Mui-focusVisible': {
        boxShadow: `0 0 0 6px ${theme.palette.primary.main}25`,
      },
    },
  },
  timeText: {
    fontSize: '0.68rem',
    color: theme.palette.text.secondary,
    minWidth: 32,
    textAlign: 'center',
    userSelect: 'none',
    fontVariantNumeric: 'tabular-nums',
  },
  rateButton: {
    fontSize: '0.65rem',
    minWidth: 'auto',
    padding: '1px 5px',
    height: 18,
    borderRadius: 9,
    textTransform: 'none',
    backgroundColor: 'rgba(0,0,0,0.08)',
    lineHeight: 1,
    '&:hover': {
      backgroundColor: 'rgba(0,0,0,0.14)',
    },
  },
  controlsContainer: {
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
    gap: theme.spacing(1)
  },
  transcriptionContainer: {
    width: '100%',
    marginTop: theme.spacing(1),
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center'
  },
  transcriptionText: {
    fontSize: '0.875rem',
    lineHeight: 1.4,
    wordBreak: 'break-word',
    padding: theme.spacing(1),
    backgroundColor: theme.palette.action.hover,
    borderRadius: theme.shape.borderRadius,
    border: `1px solid ${theme.palette.divider}`,
    width: '100%',
    boxSizing: 'border-box'
  },
  transcribeButton: {
    fontSize: '0.75rem',
    padding: theme.spacing(0.5, 1),
    minWidth: 'auto',
    height: '32px',
    alignSelf: 'center'
  },
}));

const formatTime = (seconds) => {
  if (!seconds || isNaN(seconds)) return "0:00";
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${mins}:${secs.toString().padStart(2, "0")}`;
};

const AudioModal = ({ url, message, disableTranscription = false, ticketUuid }) => {
  const theme = useTheme();
  const classes = useStyles();
  const [transcription, setTranscription] = useState(null);
  const [isTranscribing, setIsTranscribing] = useState(false);
  const [localDuration, setLocalDuration] = useState(0);

  const body = message?.body ?? "";
  const transcrito = message?.transcrito ?? false;

  // Global audio player context
  const {
    audioRef: globalAudioRef,
    currentAudio,
    isPlaying,
    audioRate,
    playAudio,
    changeRate,
  } = useContext(AudioPlayerContext);

  // Is this the currently active audio in the global player?
  const isActive = currentAudio?.url === url && currentAudio?.mediaType === "audio";

  // Local time tracking synced from global audioRef
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [seeking, setSeeking] = useState(false);
  const animFrameRef = useRef(null);

  // Hidden audio element just for loading duration metadata
  const metadataAudioRef = useRef(null);
  const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
  const sourceUrl = isIOS ? url?.replace(".ogg", ".mp3") : url;

  // Load duration from metadata of hidden audio element
  useEffect(() => {
    const audio = metadataAudioRef.current;
    if (!audio) return;

    const onLoadedMetadata = () => {
      if (audio.duration && isFinite(audio.duration)) {
        setLocalDuration(audio.duration);
      }
    };

    audio.addEventListener("loadedmetadata", onLoadedMetadata);
    if (audio.readyState >= 1 && audio.duration && isFinite(audio.duration)) {
      setLocalDuration(audio.duration);
    }

    return () => {
      audio.removeEventListener("loadedmetadata", onLoadedMetadata);
    };
  }, [url]);

  // Sync time from global player when this is the active audio
  useEffect(() => {
    if (!isActive) {
      if (animFrameRef.current) {
        cancelAnimationFrame(animFrameRef.current);
        animFrameRef.current = null;
      }
      return;
    }

    const update = () => {
      if (globalAudioRef.current && !seeking) {
        setCurrentTime(globalAudioRef.current.currentTime);
        const dur = globalAudioRef.current.duration;
        if (dur && isFinite(dur)) {
          setDuration(dur);
        }
      }
      animFrameRef.current = requestAnimationFrame(update);
    };
    animFrameRef.current = requestAnimationFrame(update);

    return () => {
      if (animFrameRef.current) cancelAnimationFrame(animFrameRef.current);
    };
  }, [isActive, globalAudioRef, seeking]);

  // Reset time display when this audio stops being active
  useEffect(() => {
    if (!isActive) {
      setCurrentTime(0);
      setDuration(0);
    }
  }, [isActive]);

  const handlePlayPause = useCallback(() => {
    if (isActive && globalAudioRef.current) {
      // Toggle play/pause on already-active audio
      if (globalAudioRef.current.paused) {
        globalAudioRef.current.play().catch(() => {});
      } else {
        globalAudioRef.current.pause();
      }
    } else {
      // Start playing this audio via global player
      const contactName = message?.contact?.name || "";
      playAudio(url, message, contactName, ticketUuid);
    }
  }, [isActive, globalAudioRef, playAudio, url, message, ticketUuid]);

  const handleSliderChange = (_, value) => {
    setSeeking(true);
    setCurrentTime(value);
  };

  const handleSliderCommitted = (_, value) => {
    if (isActive && globalAudioRef.current) {
      globalAudioRef.current.currentTime = value;
    }
    setSeeking(false);
  };

  const handleTranscribe = async () => {
    if (isTranscribing) return;
    setIsTranscribing(true);
    try {
      const { data } = await api.post(`/message/transcribeAudio`, {
        wid: message.wid,
      }, { timeout: 65000 });

      if (data) {
        setTranscription(data);
      }
    } catch (error) {
      if (error.code === 'ECONNABORTED') {
        setTranscription("Transcrição demorou demais. Tente novamente.");
      } else {
        setTranscription("Erro ao transcrever áudio. Tente novamente.");
      }
      console.error("Erro ao transcrever áudio:", error);
    } finally {
      setIsTranscribing(false);
    }
  };

  const displayDuration = isActive && duration > 0 ? duration : localDuration;
  const displayTime = isActive ? currentTime : 0;
  const showPlaying = isActive && isPlaying;

  return (
    <div className={classes.audioContainer}>
      {/* Hidden audio element for metadata/duration only */}
      <audio
        ref={metadataAudioRef}
        preload="metadata"
        src={sourceUrl}
        style={{ display: 'none' }}
      />

      <div className={classes.playerRow}>
        <IconButton
          className={classes.playButton}
          size="small"
          onClick={handlePlayPause}
        >
          {showPlaying
            ? <PauseIcon fontSize="small" />
            : <PlayArrowIcon fontSize="small" />
          }
        </IconButton>

        <Slider
          className={classes.slider}
          min={0}
          max={displayDuration || 1}
          value={displayTime}
          onChange={handleSliderChange}
          onChangeCommitted={handleSliderCommitted}
          step={0.1}
          disabled={!isActive}
        />

        <Typography className={classes.timeText}>
          {isActive
            ? formatTime(displayTime)
            : formatTime(displayDuration)
          }
        </Typography>

        <Button
          className={classes.rateButton}
          onClick={changeRate}
          size="small"
          disableRipple
        >
          {audioRate}x
        </Button>
      </div>

      <div className={classes.controlsContainer}>
        {!disableTranscription && (
          <div className={classes.transcriptionContainer}>
            {!transcrito ? (
              transcription ? (
                <Typography className={classes.transcriptionText} variant="body2">
                  <strong>Transcrição:</strong> {transcription}
                </Typography>
              ) : (
                <Button
                  onClick={handleTranscribe}
                  variant="contained"
                  className={classes.transcribeButton}
                  disabled={isTranscribing}
                  style={{
                    backgroundColor: isTranscribing
                      ? "#ccc"
                      : theme.palette.primary.main,
                    color: "#fff",
                  }}
                >
                  {isTranscribing ? "Transcrevendo..." : "Transcrever"}
                </Button>
              )
            ) : (
              <Typography className={classes.transcriptionText} variant="body2">
                <strong>Transcrição:</strong> {body}
              </Typography>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default AudioModal;
